import { createTheme } from '@mui/material/styles';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import { BB } from './styles/bbTokens';

// Define card type colors and custom design tokens as part of the theme
declare module '@mui/material/styles' {
  interface Theme {
    cardTypes: {
      BEAT: {
        primary: string;
        light: string;
        input: string;
      };
      CHARACTER: {
        primary: string;
        light: string;
        input: string;
      };
      SCENE: {
        primary: string;
        light: string;
        input: string;
      };
    };
    customTokens: {
      shadows: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        '2xl': string;
      };
      animations: {
        fast: string;
        normal: string;
        slow: string;
      };
      zIndex: {
        dropdown: number;
        sticky: number;
        fixed: number;
        modal: number;
        popover: number;
        tooltip: number;
      };
      borderRadius: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        '2xl': string;
        full: string;
      };
      gradients: {
        primary: string;
        secondary: string;
        accent: string;
        glass: string;
      };
      wizard: {
        title: {
          color: string;
          fontFamily: string;
          fontWeight: number;
          letterSpacing: string;
          gradient: string;
        };
        background: {
          color: string;
          texture: string;
          gradient: string;
        };
        instructionBox: {
          backgroundColor: string;
          border: string;
          textColor: string;
        };
        stageList: {
          background: string;
          selectedBackground: string;
          borderColor: string;
          textColor: string;
          textGradient: string;
        };
      };
      sidebar: {
        background: string;
        hoverBackground: string;
        activeBackground: string;
        activeBackgroundHover: string;
        divider: string;
        text: string;
        mutedText: string;
        headerText: string;
        accent: string;
        accentSoft: string;
        activeText: string;
        controlBackground: string;
        controlBorder: string;
      };
      landing: {
        light: {
          background: string;
          backgroundAlt: string;
          textPrimary: string;
          textSecondary: string;
          cardBackground: string;
          cardBorder: string;
        };
        accent: {
          gold: string;
          goldLight: string;
          goldGlow: string;
        };
        typography: {
          displaySize: { xs: string; md: string };
          pullQuoteSize: string;
        };
      };
    };
  }

  interface ThemeOptions {
    cardTypes?: {
      BEAT?: {
        primary?: string;
        light?: string;
        input?: string;
      };
      CHARACTER?: {
        primary?: string;
        light?: string;
        input?: string;
      };
      SCENE?: {
        primary?: string;
        light?: string;
        input?: string;
      };
    };
    customTokens?: {
      shadows?: {
        xs?: string;
        sm?: string;
        md?: string;
        lg?: string;
        xl?: string;
        '2xl'?: string;
      };
      animations?: {
        fast?: string;
        normal?: string;
        slow?: string;
      };
      zIndex?: {
        dropdown?: number;
        sticky?: number;
        fixed?: number;
        modal?: number;
        popover?: number;
        tooltip?: number;
      };
      borderRadius?: {
        xs?: string;
        sm?: string;
        md?: string;
        lg?: string;
        xl?: string;
        '2xl'?: string;
        full?: string;
      };
      gradients?: {
        primary?: string;
        secondary?: string;
        accent?: string;
        glass?: string;
      };
      wizard?: {
        title?: {
          color?: string;
          fontFamily?: string;
          fontWeight?: number;
          letterSpacing?: string;
          gradient?: string;
        };
        background?: {
          color?: string;
          texture?: string;
          gradient?: string;
        };
        instructionBox?: {
          backgroundColor?: string;
          border?: string;
          textColor?: string;
        };
        stageList?: {
          background?: string;
          selectedBackground?: string;
          borderColor?: string;
          textColor?: string;
          textGradient?: string;
        };
      };
      sidebar?: {
        background?: string;
        hoverBackground?: string;
        activeBackground?: string;
        activeBackgroundHover?: string;
        divider?: string;
        text?: string;
        mutedText?: string;
        headerText?: string;
        accent?: string;
        accentSoft?: string;
        activeText?: string;
        controlBackground?: string;
        controlBorder?: string;
      };
      landing?: {
        light?: {
          background?: string;
          backgroundAlt?: string;
          textPrimary?: string;
          textSecondary?: string;
          cardBackground?: string;
          cardBorder?: string;
        };
        accent?: {
          gold?: string;
          goldLight?: string;
          goldGlow?: string;
        };
        typography?: {
          displaySize?: { xs?: string; md?: string };
          pullQuoteSize?: string;
        };
      };
    };
  }
}

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BB.gold,
      light: '#e8c97a',
      dark: '#b8913f',
      contrastText: BB.onGold,
    },
    secondary: {
      main: BB.inkDim,
      light: BB.ink,
      dark: BB.inkFaint,
      contrastText: BB.bg,
    },
    background: {
      default: BB.bgMain,
      paper: BB.surface,
    },
    text: {
      primary: BB.ink,
      secondary: BB.inkDim,
    },
    divider: BB.line,
    grey: {
      50: '#0c0b0e',
      100: '#15121a',
      200: '#1a1622',
      300: '#2a2530',
      400: '#4a453e',
      500: '#7f7a72',
      600: '#b7b2a8',
      700: '#d6d1c6',
      800: '#f4f1ea',
      900: '#faf7ef',
    },
  },
  typography: {
    fontFamily: BB.font.body,
    h1: {
      fontFamily: BB.font.display,
      fontSize: '3rem',
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: BB.font.display,
      fontSize: '2.4rem',
      fontWeight: 600,
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: BB.font.display,
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: BB.font.display,
      fontSize: '1.6rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily: BB.font.display,
      fontSize: '1.3rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: BB.font.display,
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.65,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
  },
  cardTypes: {
    BEAT: {
      primary: '#0b0b0c',   // Near-black base for cards
      light: '#0c0c0e',     // Slightly lighter for linked
      input: '#0b0b0c',     // Uniform dark input background
    },
    CHARACTER: {
      primary: '#0b0b0c',
      light: '#0c0c0e',
      input: '#0b0b0c',
    },
    SCENE: {
      primary: '#0b0b0c',
      light: '#0c0c0e',
      input: '#0b0b0c',
    },
  },
  customTokens: {
    shadows: {
      xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
      sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)',
    },
    animations: {
      fast: '0.1s ease-in-out',
      normal: '0.2s ease-in-out',
      slow: '0.3s ease-in-out',
    },
    zIndex: {
      dropdown: 1000,
      sticky: 1020,
      fixed: 1030,
      modal: 1040,
      popover: 1050,
      tooltip: 1060,
    },
    borderRadius: {
      xs: '2px',
      sm: '4px',
      md: '8px',
      lg: '12px',
      xl: '16px',
      '2xl': '24px',
      full: '9999px',
    },
    gradients: {
      primary: 'linear-gradient(135deg, #d4a853 0%, #e8c97a 100%)',
      secondary: 'linear-gradient(135deg, #b8913f 0%, #d4a853 100%)',
      accent: 'linear-gradient(135deg, #d4a853 0%, #b8913f 100%)',
      glass: 'linear-gradient(135deg, rgba(244, 241, 234, 0.08) 0%, rgba(244, 241, 234, 0.04) 100%)',
    },
    landing: {
      light: {
        background: '#FAF8F5',
        backgroundAlt: '#F2EDE8',
        textPrimary: '#1a1a2e',
        textSecondary: '#4a4a5a',
        cardBackground: '#ffffff',
        cardBorder: 'rgba(0, 0, 0, 0.08)',
      },
      accent: {
        gold: '#D4A853',
        goldLight: '#E8C97A',
        goldGlow: 'rgba(212, 168, 83, 0.3)',
      },
      typography: {
        displaySize: { xs: '3rem', md: '5rem' },
        pullQuoteSize: '1.5rem',
      },
    },
    wizard: {
      title: {
        color: BB.ink,
        fontFamily: BB.font.display,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        gradient: 'linear-gradient(135deg, #d4a853 0%, #e8c97a 50%, #d4a853 100%)',
      },
      background: {
        color: 'background.default',
        texture: 'none',
        gradient: 'linear-gradient(180deg, rgba(212, 168, 83, 0.03) 0%, rgba(212, 168, 83, 0.03) 100%)',
      },
      instructionBox: {
        backgroundColor: BB.goldWash,
        border: `1px solid ${BB.goldDim}`,
        textColor: BB.inkDim,
      },
      stageList: {
        background: BB.surface,
        selectedBackground: BB.goldWash,
        borderColor: BB.gold,
        textColor: BB.ink,
        textGradient: 'linear-gradient(135deg, #d4a853 0%, #e8c97a 50%, #d4a853 100%)',
      },
    },
    sidebar: {
      background: BB.bg,
      hoverBackground: BB.goldWash,
      activeBackground: BB.goldWash,
      activeBackgroundHover: 'rgba(212, 168, 83, 0.12)',
      divider: BB.line,
      text: BB.inkDim,
      mutedText: BB.inkFaint,
      headerText: BB.inkFaint,
      accent: BB.gold,
      accentSoft: BB.goldDim,
      activeText: BB.gold,
      controlBackground: 'transparent',
      controlBorder: BB.line2,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 999,
          textTransform: 'none',
          fontFamily: BB.font.body,
          fontWeight: 500,
          fontSize: '0.9rem',
          padding: '8px 18px',
          boxShadow: 'none',
          transition: `all ${theme.customTokens.animations.normal}`,
          '&:hover': {
            boxShadow: 'none',
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${BB.fieldFocusRing}`,
          },
        }),
        contained: () => ({
          background: BB.gold,
          color: BB.onGold,
          fontWeight: 600,
          '&:hover': {
            background: BB.gold,
            boxShadow: '0 8px 22px rgba(212, 168, 83, 0.22)',
            transform: 'translateY(-1px)',
          },
        }),
        outlined: () => ({
          borderWidth: '1px',
          borderColor: BB.line2,
          color: BB.ink,
          '&:hover': {
            borderWidth: '1px',
            borderColor: BB.inkDim,
            backgroundColor: BB.goldWash,
          },
        }),
        text: () => ({
          color: BB.inkDim,
          '&:hover': {
            color: BB.ink,
            backgroundColor: BB.goldWash,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          color: BB.inkFaint,
          transition: `all ${theme.customTokens.animations.normal}`,
          '&:hover': {
            backgroundColor: BB.goldWash,
            color: BB.ink,
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${BB.fieldFocusRing}`,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 0,
          boxShadow: BB.shadowCard,
          border: `1px solid ${BB.line}`,
          backgroundColor: BB.surface,
          backgroundImage: 'none',
          transition: `border-color ${theme.customTokens.animations.normal}`,
          '&:hover': {
            borderColor: BB.line2,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 999,
          fontFamily: BB.font.mono,
          fontWeight: 400,
          fontSize: '0.66rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          padding: '3px 4px',
          height: 'auto',
          backgroundColor: 'transparent',
          border: `1px solid ${BB.line2}`,
          color: BB.inkFaint,
          transition: `all ${theme.customTokens.animations.normal}`,
          '&:hover': {
            borderColor: BB.goldDim,
            backgroundColor: BB.goldWash,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: BB.surface2,
          color: BB.ink,
          fontSize: '0.72rem',
          fontWeight: 500,
          padding: '4px 9px',
          borderRadius: 7,
          border: `1px solid ${BB.line2}`,
          boxShadow: theme.customTokens.shadows.lg,
          maxWidth: 240,
          transition: `opacity ${theme.customTokens.animations.fast}`,
        }),
        arrow: {
          color: BB.surface2,
          '&::before': {
            border: `1px solid ${BB.line2}`,
          },
        },
      },
    },
    /* Editable text = mono + brightest ink + gold focus. The core convention. */
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: BB.font.mono,
          fontSize: '0.86rem',
          lineHeight: 1.7,
          color: BB.inkEdit,
          transition: `border-color ${theme.customTokens.animations.normal}, box-shadow ${theme.customTokens.animations.normal}`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: BB.fieldBorder,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BB.fieldBorderHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BB.gold,
            borderWidth: 1,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${BB.fieldFocusRing}`,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontFamily: BB.font.mono,
          color: BB.inkEdit,
          '&::placeholder': {
            fontFamily: BB.font.body,
            color: BB.inkFaint,
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: BB.font.mono,
          fontSize: '0.78rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: BB.inkFaint,
          '&.Mui-focused': {
            color: BB.gold,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${BB.goldDim}`,
          backgroundColor: BB.goldWash,
          color: BB.inkDim,
          '& .MuiAlert-icon': {
            color: BB.gold,
          },
        },
        /* Errors keep semantic red — the single-gold-accent rule covers brand
           chrome, not severity semantics. */
        standardError: {
          border: '1px solid rgba(239, 68, 68, 0.5)',
          backgroundColor: 'rgba(239, 68, 68, 0.08)',
          '& .MuiAlert-icon': {
            color: '#f87171',
          },
        },
        filledError: {
          border: '1px solid rgba(239, 68, 68, 0.5)',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          color: BB.ink,
          '& .MuiAlert-icon': {
            color: '#f87171',
          },
        },
        outlinedError: {
          border: '1px solid rgba(239, 68, 68, 0.5)',
          backgroundColor: 'transparent',
          '& .MuiAlert-icon': {
            color: '#f87171',
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: Fade,
        TransitionProps: { timeout: 200 },
      },
      styleOverrides: {
        paper: {
          borderRadius: 4,
          border: `1px solid ${BB.line2}`,
          backgroundColor: BB.surface,
          backgroundImage: 'none',
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        TransitionComponent: Grow,
        TransitionProps: { timeout: 180 },
      },
      styleOverrides: {
        paper: {
          borderRadius: 6,
          border: `1px solid ${BB.line2}`,
          backgroundColor: BB.surface2,
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        TransitionComponent: Grow,
        TransitionProps: { timeout: 180 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: BB.line,
        },
      },
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
});
